// ===============================================================
// 💘 MatchRadarPage.js — LIVORA Match Radar Page (FINAL COUNTRIES DIRECT)
// - نفس المنطق الحالي 100%
// - الفيديو هو الخلفية النهائية
// - عند الضغط على زر التصفية تظهر الدول مباشرة
// - بدون اختيار وضع المطابقة داخل الواجهة
// - البحث يعمل دائمًا بوضع smart
// ===============================================================

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { CountryFlagGlyph } from "../utils/countryFlags";
import {
  buildReturnContextFromLocation,
  saveReturnContext,
} from "../calls/nativeCallReturnContext";
import { resolveCallRoomRoute } from "../calls/callRoute";
import "./MatchRadarPage.css";

const SEARCH_WINDOW_MS = 20000;
const RETRY_INTERVAL_MS = 3000;

// -------------------------------------------------------------
// 🌍 Countries list
// -------------------------------------------------------------
const allCountriesList = [
  { key: "sa", name: "السعودية" },
  { key: "kw", name: "الكويت" },
  { key: "bh", name: "البحرين" },
  { key: "tr", name: "تركيا" },
  { key: "ru", name: "روسيا" },
  { key: "ua", name: "أوكرانيا" },
  { key: "at", name: "النمسا" },
  { key: "de", name: "ألمانيا" },
  { key: "fr", name: "فرنسا" },
  { key: "it", name: "إيطاليا" },
  { key: "es", name: "إسبانيا" },
  { key: "se", name: "السويد" },
  { key: "sy", name: "سوريا" },
  { key: "jo", name: "الأردن" },
  { key: "eg", name: "مصر" },
  { key: "tn", name: "تونس" },
  { key: "dz", name: "الجزائر" },
  { key: "ma", name: "المغرب" },
];

export default function MatchRadarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const functions = getFunctions(undefined, "us-central1");

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [statusText, setStatusText] = useState(t("matchmaking.status.ready", { defaultValue: "Tap to start searching" }));
  const [preferredCountry, setPreferredCountry] = useState("");
  const [error, setError] = useState("");
  const [remainingMs, setRemainingMs] = useState(SEARCH_WINDOW_MS);
  const [filterOpen, setFilterOpen] = useState(false);

  const cancelledRef = useRef(false);
  const retryTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const searchStartedAtRef = useRef(null);

  const startMatchingFn = httpsCallable(functions, "startMatching");
  const cancelMatchingFn = httpsCallable(functions, "cancelMatching");

  const selectedCountry = allCountriesList.find(
    (item) => item.key === preferredCountry
  );

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearRetryTimer();
      clearCountdownTimer();
    };
  }, []);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const clearCountdownTimer = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const stopAllSearchTimers = () => {
    clearRetryTimer();
    clearCountdownTimer();
  };

  const formatRemaining = (ms) => {
    const safe = Math.max(0, Math.ceil(Number(ms || 0) / 1000));
    return t("matchmaking.seconds_short", { count: safe, defaultValue: "{{count}}s" });
  };

  const deriveStatusText = (ms) => {
    const sec = Math.max(0, Math.ceil(Number(ms || 0) / 1000));

    if (sec > 14) {
      return t("matchmaking.status.searching", { remaining: formatRemaining(ms), defaultValue: "Searching for a suitable match... ({{remaining}})" });
    }

    if (sec > 7) {
      return t("matchmaking.status.still_searching", { remaining: formatRemaining(ms), defaultValue: "Still searching for the best match... ({{remaining}})" });
    }

    if (sec > 0) {
      return t("matchmaking.status.almost_done", { remaining: formatRemaining(ms), defaultValue: "Almost done... trying to find a match now ({{remaining}})" });
    }

    return t("matchmaking.status.expired", { defaultValue: "Search time expired" });
  };

  const deriveServerReasonText = (reason, ms) => {
    switch (String(reason || "")) {
      case "female_waiting_for_male_match":
        return t("matchmaking.status.female_waiting_for_male_match", { remaining: formatRemaining(ms), defaultValue: "You are waiting for a suitable man to find you... ({{remaining}})" });

      case "no_candidates_online":
        return t("matchmaking.status.no_candidates_online", { remaining: formatRemaining(ms), defaultValue: "No suitable users are online right now... ({{remaining}})" });

      case "no_match_found_now":
        return deriveStatusText(ms);

      case "search_window_expired":
        return t("matchmaking.status.expired", { defaultValue: "Search time expired" });

      default:
        return deriveStatusText(ms);
    }
  };

  const beginCountdown = () => {
    clearCountdownTimer();

    countdownTimerRef.current = setInterval(() => {
      if (!searchStartedAtRef.current) return;

      const elapsed = Date.now() - searchStartedAtRef.current;
      const left = Math.max(0, SEARCH_WINDOW_MS - elapsed);

      setRemainingMs(left);

      if (left <= 0) {
        clearCountdownTimer();
      }
    }, 250);
  };

  const finalizeSearchAsStopped = (text) => {
    stopAllSearchTimers();
    setSearching(false);
    setLoading(false);
    setStatusText(text);
  };

  const goBack = async () => {
    try {
      cancelledRef.current = true;
      stopAllSearchTimers();

      if (searching) {
        await cancelMatchingFn();
      }
    } catch (e) {
      console.error("cancel matching on back error:", e);
    } finally {
      navigate(-1);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      cancelledRef.current = true;
      stopAllSearchTimers();

      await cancelMatchingFn();

      setSearching(false);
      setStatusText(t("matchmaking.status.cancelled", { defaultValue: "Search cancelled" }));
      setError("");
      setRemainingMs(SEARCH_WINDOW_MS);
    } catch (e) {
      console.error("cancel matching error:", e);
      setError(t("matchmaking.errors.cancel_failed", { defaultValue: "Could not cancel search" }));
    } finally {
      setLoading(false);
    }
  };

  const scheduleRetry = () => {
    clearRetryTimer();

    retryTimerRef.current = setTimeout(() => {
      tryMatch();
    }, RETRY_INTERVAL_MS);
  };

  const resolveMatchedRoute = (data, currentUid) => {
    const maleUid = String(data?.maleUid || "");
    const femaleUid = String(data?.femaleUid || "");
    const payerUid = String(data?.payerUid || "");
    const matchInitiatorUid = String(data?.matchInitiatorUid || "");

    if (
      currentUid &&
      (currentUid === maleUid ||
        currentUid === payerUid ||
        currentUid === matchInitiatorUid)
    ) {
      return `/call-outgoing/${data.callId}`;
    }

    if (currentUid && currentUid === femaleUid) {
      const { target } = resolveCallRoomRoute({
        callId: data.callId,
        role: "receiver",
        source: "match_radar_matched",
      });
      return target;
    }

    return `/call-outgoing/${data.callId}`;
  };

  const tryMatch = async () => {
    if (cancelledRef.current) return;

    const currentUser = auth.currentUser;
    const currentUid = currentUser?.uid || "";

    const startedAt = searchStartedAtRef.current || Date.now();
    const elapsed = Date.now() - startedAt;
    const leftBeforeCall = Math.max(0, SEARCH_WINDOW_MS - elapsed);

    if (leftBeforeCall <= 0) {
      try {
        await cancelMatchingFn();
      } catch (e) {
        console.error("cancel on timeout error:", e);
      }

      finalizeSearchAsStopped(t("matchmaking.status.no_match_within_window", { defaultValue: "No match was found within 20 seconds" }));
      setRemainingMs(0);
      return;
    }

    try {
      const payload = {
        matchMode: "smart",
        preferredCountry: preferredCountry.trim() || null,
      };

      const res = await startMatchingFn(payload);
      const data = res?.data || {};

      if (cancelledRef.current) return;

      if (data?.matched && data?.callId) {
        stopAllSearchTimers();
        setSearching(false);
        setStatusText(t("matchmaking.status.matched", { defaultValue: "Match found!" }));
        setError("");
        setFilterOpen(false);

        const nextRoute = resolveMatchedRoute(data, currentUid);
        const returnContext = buildReturnContextFromLocation(location);
        if (currentUid && data.callId && returnContext) {
          saveReturnContext(currentUid, data.callId, returnContext);
          console.log(
            `[callReturn] captured role=caller callId=${data.callId} url=${returnContext.url}`
          );
        }

        navigate(nextRoute, {
          replace: true,
          state: {
            returnTo: returnContext?.url || "/match-radar",
            returnContext: returnContext || undefined,
          },
        });
        return;
      }

      if (data?.searchExpired) {
        try {
          await cancelMatchingFn();
        } catch (e) {
          console.error("cancel after searchExpired error:", e);
        }

        finalizeSearchAsStopped(t("matchmaking.status.no_match_within_window", { defaultValue: "No match was found within 20 seconds" }));
        setRemainingMs(0);
        return;
      }

      if (data?.searching) {
        setSearching(true);
        setError("");

        const nextRemaining =
          typeof data?.remainingMs === "number"
            ? Math.max(0, data.remainingMs)
            : Math.max(0, SEARCH_WINDOW_MS - (Date.now() - startedAt));

        setRemainingMs(nextRemaining);
        setStatusText(deriveServerReasonText(data?.reason, nextRemaining));

        if (nextRemaining <= 0) {
          try {
            await cancelMatchingFn();
          } catch (e) {
            console.error("cancel after zero remaining error:", e);
          }

          finalizeSearchAsStopped(t("matchmaking.status.no_match_within_window", { defaultValue: "No match was found within 20 seconds" }));
          return;
        }

        scheduleRetry();
        return;
      }

      finalizeSearchAsStopped(t("matchmaking.status.no_match_now", { defaultValue: "No match was found right now" }));
    } catch (e) {
      console.error("start matching error:", e);

      const msg =
        e?.message || e?.details || t("matchmaking.errors.search_failed", { defaultValue: "An error occurred while searching for a match" });

      if (String(msg).includes(t("matchmaking.errors.active_call_server", { defaultValue: "You already have an active call or open call request." }))) {
        stopAllSearchTimers();
        setSearching(false);
        setLoading(false);
        setError("");
        setStatusText(t("matchmaking.status.call_found", { defaultValue: "Call found..." }));
        return;
      }

      setError(msg);
      finalizeSearchAsStopped(t("matchmaking.errors.start_failed", { defaultValue: "Could not start search" }));
    }
  };

  const handleStart = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate("/");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSearching(true);
      setRemainingMs(SEARCH_WINDOW_MS);
      setStatusText(t("matchmaking.status.preparing", { defaultValue: "Preparing search..." }));
      setFilterOpen(false);

      cancelledRef.current = false;
      searchStartedAtRef.current = Date.now();

      stopAllSearchTimers();
      beginCountdown();

      await tryMatch();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="match-radar-page">
      <div className="video-bg">
        <video
          src="/videos/match-bg.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="video-element"
        />
      </div>

      <div className="video-overlay" />

      <div className="match-topbar">
        <button type="button" onClick={goBack} className="icon-btn">
          ✕
        </button>

        <div className="title-wrap">
          <div className="page-title">{t("matchmaking.title", { defaultValue: "Matches" })}</div>
          <div className="page-subtitle">{t("matchmaking.subtitle", { defaultValue: "Find a new call quickly and safely" })}</div>
        </div>

        <div className="safe-chip">{t("matchmaking.safe", { defaultValue: "Safe" })}</div>
      </div>

      <div className="match-content" />

      <div className="bottom-panel">
        <div className="text-block">
          <h1 className="main-text">
            {searching ? t("matchmaking.searching_title", { defaultValue: "Searching..." }) : t("matchmaking.start_now", { defaultValue: "Start now" })}
          </h1>

          <p className="secondary-text">{statusText}</p>

          {searching ? (
            <div className="search-dots">
              <span />
              <span />
              <span />
            </div>
          ) : null}

          <div className="meta-row">
            {preferredCountry ? (
              <div className="meta-chip">
                {selectedCountry ? (
                  <CountryFlagGlyph
                    countryCode={selectedCountry.key}
                    className="country-flag"
                    imgClassName="country-flag country-flag-glyph__img"
                  />
                ) : (
                  <CountryFlagGlyph className="country-flag" />
                )}{" "}
                {selectedCountry ? selectedCountry.key.toUpperCase() : preferredCountry}
              </div>
            ) : (
              <div className="meta-chip">
                🌍 {t("matchmaking.all_countries", { defaultValue: "All countries" })}
              </div>
            )}
          </div>

          {searching ? (
            <div className="timer-chip">
              {t("matchmaking.remaining_time", { remaining: formatRemaining(remainingMs), defaultValue: "Remaining time: {{remaining}}" })}
            </div>
          ) : null}

          {error ? <p className="error-text">{error}</p> : null}
        </div>

        <div className="bottom-actions bottom-actions-split">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="filter-btn"
            disabled={loading}
          >
            {t("matchmaking.filter", { defaultValue: "Filter" })}
          </button>

          {!searching ? (
            <button
              type="button"
              onClick={handleStart}
              disabled={loading}
              className="primary-btn"
            >
              {loading ? t("matchmaking.starting", { defaultValue: "Starting..." }) : t("matchmaking.start_search", { defaultValue: "Start search" })}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="secondary-btn"
            >
              {loading ? t("matchmaking.cancelling", { defaultValue: "Cancelling..." }) : t("matchmaking.cancel_search", { defaultValue: "Cancel search" })}
            </button>
          )}
        </div>
      </div>

      {filterOpen ? (
        <>
          <div
            className="sheet-backdrop"
            onClick={() => setFilterOpen(false)}
          />

          <div className="filter-sheet">
            <div className="sheet-handle" />

            <div className="sheet-header">
              <div className="sheet-title">{t("matchmaking.choose_country", { defaultValue: "Choose country" })}</div>
              <button
                type="button"
                className="sheet-close"
                onClick={() => setFilterOpen(false)}
              >
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div className="sheet-body">
              <div className="countries-grid countries-grid-single">
                <button
                  type="button"
                  className={`country-chip ${
                    preferredCountry === "" ? "active" : ""
                  }`}
                  onClick={() => setPreferredCountry("")}
                  disabled={loading || searching}
                >
                  <span className="country-flag">🌍</span>
                  <span className="country-name">{t("matchmaking.all_countries", { defaultValue: "All countries" })}</span>
                </button>

                {allCountriesList.map((country) => (
                  <button
                    key={country.key}
                    type="button"
                    className={`country-chip ${
                      preferredCountry === country.key ? "active" : ""
                    }`}
                    onClick={() =>
                      setPreferredCountry(
                        preferredCountry === country.key ? "" : country.key
                      )
                    }
                    disabled={loading || searching}
                  >
                    <CountryFlagGlyph
                      countryCode={country.key}
                      className="country-flag"
                      imgClassName="country-flag country-flag-glyph__img"
                    />
                    <span className="country-name">{country.key.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="sheet-footer">
              <button
                type="button"
                className="sheet-apply-btn"
                onClick={() => setFilterOpen(false)}
              >
                {t("common.done", { defaultValue: "Done" })}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}  
